<template>
  <section
    ref="calendarSection"
    class="relative pt-8 lg:pt-16"
    aria-labelledby="calendar-view-heading"
    :aria-busy="loading"
  >
    <h1 id="calendar-view-heading" class="sr-only">{{ settingStore.venue_name }} Calendar</h1>

    <div v-if="loading" class="sr-only" role="status">Loading calendar</div>

    <Spinner :display="loading" />

    <div
      v-if="!loading"
      class="flex flex-col justify-start items-start gap-8 md:gap-16 lg:flex-row lg:gap-24 w-full px-4 max-w-screen-2xl mx-auto"
    >
      <CalendarGrid
        id="calendar-grid"
        v-model="selectedDate"
        :dates-to-highlight="datesToHighlight"
        :min-date="minDate"
        :max-date="maxDate"
        class="calendar-grid-slot w-full lg:max-w-md lg:sticky lg:top-0"
      />

      <div
        v-if="selectedDate"
        ref="showDetailsRegion"
        role="region"
        aria-labelledby="show-details-heading"
        class="w-full"
      >
        <h2 id="show-details-heading" class="sr-only">Show details</h2>
        <div class="sr-only live-message" role="status" aria-live="polite" aria-atomic="true">
          {{ liveAnnouncement }}
        </div>
        <template v-if="displaysShowsForSelectedDate.length > 0">
          <ShowCard
            v-for="show in displaysShowsForSelectedDate"
            :key="`show-${show.id}-${show.date}`"
            class="show-card border-b first:pt-0 last:border-b-0 border-linear-g-primary py-12 md:py-16 md:gap-10"
            :show="show"
            :ui="ui"
            :headingLevel="3"
            descriptionDisplayType="long"
          />
        </template>
        <p v-else class="text-fg-g-primary font-sans">No shows on this date.</p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { last, uniq } from 'lodash-es'
import { computed, onMounted, onServerPrefetch, ref, watch } from 'vue'

import * as api from '@/api/api'
import Spinner from '@/common/components/atoms/Spinner.vue'
import { StorePerformance, parsePerformance } from '@/models'
import CalendarGrid from '@/modules/guestUi/components/organisms/CalendarGrid.vue'
import ShowCard from '@/modules/guestUi/components/organisms/ShowCard.vue'
import { useDisplayShows } from '@/modules/guestUi/composables/useDisplayShows'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { blankError } from '@/utils/errors'
import { buildPageTitle } from '@/utils/pageTitle'

const ui = ref({ error: blankError() })

const settingStore = useSettingStore()
const checkoutStore = useCheckoutStore()
const calendarPerformances = ref<StorePerformance[]>([])

onServerPrefetch(function setCalendarPageTitle(): Promise<void> {
  settingStore.ssrTitle = buildPageTitle(['Calendar'], settingStore)
  settingStore.ssrPrefetchDone = true
  return Promise.resolve()
})
const nowDateIsoString = computed<string>(() => settingStore.dateIsoStr(settingStore.nowDateTime()))
const performances = computed<StorePerformance[]>(() =>
  calendarPerformances.value.filter(p => {
    const published =
      !p.publish_datetime ||
      settingStore.parseMoment(p.publish_datetime).isSameOrBefore(settingStore.nowDateTime())
    return published && checkoutStore.isPerformanceVisibleOnTheList(p)
  }),
)

const selectedDate = ref<string>(nowDateIsoString.value)
const loading = ref<boolean>(true)
const displaysShowsForSelectedDate = useDisplayShows(
  computed(() => performances.value),
  {
    filter: p => settingStore.dateIsoStr(p.date) === selectedDate.value,
  },
)
const datesToHighlight = computed<string[]>(() => {
  return uniq(performances.value.map(p => settingStore.dateIsoStr(p.date))).sort()
})
const minDate = computed<string>(() => nowDateIsoString.value)
const maxDate = computed<string>(() => {
  const lastDate = last(uniq(performances.value.map(p => settingStore.dateIsoStr(p.date))).sort())
  return lastDate || nowDateIsoString.value
})

const liveAnnouncement = ref<string>('')
const hasClientLoaded = ref<boolean>(false)
function buildLiveAnnouncement(): string {
  const showCount = displaysShowsForSelectedDate.value.length
  const formattedDate = settingStore.formatTemporal(selectedDate.value, 'MMMM DD, YYYY')
  if (showCount === 0) return `No shows on ${formattedDate}`
  else if (showCount === 1) return `1 show loaded for ${formattedDate}`
  else return `${showCount} shows loaded for ${formattedDate}`
}

onMounted(async function loadPerformancesAndCalendar(): Promise<void> {
  await checkoutStore.abandonAndClear()
  const response = await api.getPerformances({
    booking: 'true',
    pagination: 'false',
  })
  calendarPerformances.value = response.data.results.map(parsePerformance)
  loading.value = false
  hasClientLoaded.value = true
  liveAnnouncement.value = buildLiveAnnouncement()
})

watch([selectedDate, () => displaysShowsForSelectedDate.value.length], () => {
  if (!hasClientLoaded.value) return
  liveAnnouncement.value = buildLiveAnnouncement()
})
</script>
